import { createActionGroup, emptyProps, props } from "@ngrx/store";
import {
  MipsPacket,
  MipsQuestionnaire,
  EligibilitySelection,
  TinEligibilityDetails,
  MipsSettings,
  QualityCategory,
  DrcfStatus,
  SubmitPacketResponse,
  DrcfDocument,
} from "@models/mips-stepper.model";
import {
  QualityMeasurementDto,
  SaveQualityMeasurementsResponse,
  GetSubmissionScoreResponse,
  SavePIMeasurementsResponse,
  SaveIAMeasurementsResponse,
} from "@services/mips-api.service";
import { DashboardProviderProd } from "@models/dashboard-providers.model";
import { OrganizationTin } from "@models/organizationTin.model";
import { DashboardFilterRequest } from "@models/dashboard-filter.model";
import { DashboardMeasureDetailProd } from "@models/dashboard-measure-details.model";
import { ReweightingResult } from "@models/mips-reweighting.model";

export const MipsPacketActions = createActionGroup({
  source: "MIPS Packet",
  events: {
    // ============================================
    // PACKET LIFECYCLE
    // ============================================
    "Get or Create Packet": props<{ eligibility: EligibilitySelection }>(),
    "Get or Create Packet Success": props<{ packet: MipsPacket }>(),
    "Get or Create Packet Failure": props<{ error: string }>(),

    "Load Existing Packet": props<{ packetId: number }>(),
    "Load Existing Packet Success": props<{ packet: MipsPacket }>(),
    "Load Existing Packet Failure": props<{ error: string }>(),

    "Load Both Packets": props<{ traditionalPacketId: number; mvpPacketId: number }>(),
    "Load Both Packets Success": props<{ traditionalPacket: MipsPacket; mvpPacket: MipsPacket }>(),
    "Load Both Packets Failure": props<{ error: string }>(),

    "Reset Packet": emptyProps(),
    "Clear Packet": emptyProps(),
    "Clear Error": emptyProps(),

    "Load Providers For Selected Practice": props<{ orgId: number }>(),
    "Load Providers For Selected Practice Success": props<{
      providers: DashboardProviderProd[];
    }>(),
    "Load Providers For Selected Practice Failure": props<{ error: string }>(),

    "Load Tins For Practice": props<{ orgId: number }>(),
    "Load Tins For Practice Success": props<{ tins: OrganizationTin[] }>(),
    "Load Tins For Practice Failure": props<{ error: string }>(),

    // ============================================
    // MVP REGISTRATION
    // ============================================

    "Refresh MVP Registration": emptyProps(),
    "Refresh MVP Registration Success": props<{
      submissionId: number;
      mvpRegistered: boolean;
      mvpId?: string;
    }>(),
    "Refresh MVP Registration Failure": props<{ error: string }>(),

    // ============================================
    // MULTI-PACKET MODE
    // ============================================

    "Set Multi Packet Mode": props<{
      mode: "single" | "both";
      currentType?: "traditional" | "mvp";
      nextType?: "traditional" | "mvp";
      traditionalPacketId?: number;
      mvpPacketId?: number;
      existingTraditionalPacketId?: number;
      existingMvpPacketId?: number;
    }>(),
    "Clear Multi Packet Mode": emptyProps(),

    // ============================================
    // NAVIGATION
    // ============================================

    "Set Current Step": props<{ stepNumber: number }>(),
    "Navigate To Step": props<{ stepIndex: number }>(),

    // ============================================
    // STEP 3 - TIN DETAILS
    // ============================================

    "Save TIN Details": props<{
      packetId: number;
      tinDetails: TinEligibilityDetails;
    }>(),
    "Save TIN Details Success": props<{ tinDetails: TinEligibilityDetails }>(),
    "Save TIN Details Failure": props<{ error: string }>(),

    "Upload TIN Document": props<{ packetId: number; file: File }>(),
    "Upload TIN Document Success": props<{
      documentUrl: string;
      documentName: string;
    }>(),
    "Upload TIN Document Failure": props<{ error: string }>(),

    "Delete TIN Document": props<{ packetId: number }>(),
    "Delete TIN Document Success": emptyProps(),
    "Delete TIN Document Failure": props<{ error: string }>(),

    "Request TIN Approval": props<{ packetId: number }>(),
    "Request TIN Approval Success": emptyProps(),
    "Request TIN Approval Failure": props<{ error: string }>(),

    "Approve TIN": props<{ packetId: number }>(),
    "Approve TIN Success": props<{ tinDetails: TinEligibilityDetails }>(),
    "Approve TIN Failure": props<{ error: string }>(),

    "Reject TIN": props<{ packetId: number; reason: string }>(),
    "Reject TIN Success": props<{ tinDetails: TinEligibilityDetails }>(),
    "Reject TIN Failure": props<{ error: string }>(),

    // ============================================
    // STEP 4 - SETTINGS
    // ============================================
    "Validate CEHRT ID": props<{ cehrtId: string }>(),
    "Validate CEHRT ID Success": props<{ isValid: boolean }>(),
    "Validate CEHRT ID Failure": props<{ error: string }>(),
    "Reset CEHRT Validation": emptyProps(),

    "Save Settings": props<{ settings: MipsSettings }>(),
    "Save Settings Success": props<{ settings: MipsSettings }>(),
    "Save Settings Failure": props<{ error: string }>(),

    // ============================================
    // STEP 5 - QUALITY MEASUREMENTS
    // ============================================
    "Load Quality Measures": props<{ orgId: number; filter: DashboardFilterRequest }>(),
    "Load Quality Measures Success": props<{ measures: DashboardMeasureDetailProd[] }>(),
    "Load Quality Measures Failure": props<{ error: string }>(),

    "Load Packet Org Questionnaire": props<{ orgId: string; performanceYear: number }>(),
    "Load Packet Org Questionnaire Success": props<{ questionnaire: MipsQuestionnaire }>(),
    "Load Packet Org Questionnaire Failure": props<{ error: string }>(),

    "Load Mips Reweighting": props<{ orgId: string; performanceYear: number }>(),
    "Load Mips Reweighting Success": props<{ data: ReweightingResult }>(),
    "Load Mips Reweighting Failure": props<{ error: string }>(),

    "Save Quality Measurements": props<{
      measurements: QualityMeasurementDto[];
      quality: QualityCategory;
      reportingType: string;
      traditionalMeasureIds: string[];
      mvpMeasureIds: string[];
    }>(),
    "Save Quality Measurements Success": props<{
      response: SaveQualityMeasurementsResponse;
      mvpResponse?: SaveQualityMeasurementsResponse;
      quality: QualityCategory;
      traditionalMeasureIds: string[];
      mvpMeasureIds: string[];
      reportingType: string;
    }>(),
    "Save Quality Measurements Failure": props<{ error: string }>(),

    // ============================================
    // STEP 6 - PROMOTING INTEROPERABILITY (PI)
    // ============================================

    "Save PI Attestations": props<{
      packetId: number;
      attestations: {
        piAuditAttestation: boolean;
        oncDirectReview: "yes" | "no" | "" | null;
        interoperabilityAttestation: "yes" | "no" | "" | null;
        securityRiskAnalysis: "yes" | "no" | "" | null;
        securityRiskAnalysisImplemented: "yes" | "no" | "" | null;
        saferGuidesAttestation: "yes" | "no" | "" | null;
      };
      performanceStart: Date;
      performanceEnd: Date;
      organizationId: number;
      performanceYear: number;
      currentMeasures: Array<{
        measureId: string;
        valueType: string;
        booleanValue?: boolean | null;
        numerator?: number | null;
        denominator?: number | null;
        activeEngagementLevel?: string | null;
        exclusionId?: string | null;
      }>;
      fullMeasures: any[];
      exclusionIds: string[];
    }>(),
    "Save PI Attestations Success": props<{
      response: any;
      performanceStart: string;
      performanceEnd: string;
    }>(),
    "Save PI Attestations Failure": props<{ error: string }>(),

    "Save PI Measurements": props<{
      packetId: number;
      performanceStart: Date;
      performanceEnd: Date;
      measurements: Array<{
        measureId: string;
        valueType: string;
        booleanValue?: boolean | null;
        numerator?: number | null;
        denominator?: number | null;
        activeEngagementLevel?: string | null;
        exclusionId?: string | null;
      }>;
      fullMeasures: any[];
      exclusionIds: string[];
    }>(),
    "Save PI Measurements Success": props<{
      response: SavePIMeasurementsResponse;
      fullMeasures: any[];
      exclusionIds: string[];
    }>(),
    "Save PI Measurements Failure": props<{ error: string }>(),

    "Upload PI Document": props<{ packetId: number; file: File }>(),
    "Upload PI Document Success": props<{
      documentId: number;
      fileName: string;
      documentUrl: string;
    }>(),
    "Upload PI Document Failure": props<{ error: string }>(),

    "Download PI Document": props<{
      packetId: number;
      documentId: number;
      documentName: string;
      asAttachment: boolean;
    }>(),
    "Download PI Document Success": props<{ blob: Blob; fileName: string }>(),
    "Download PI Document Failure": props<{ error: string }>(),

    "View PI Document": props<{
      packetId: number;
      documentId: number;
      documentName: string;
    }>(),

    "Delete PI Document": props<{ packetId: number; documentId: number }>(),
    "Delete PI Document Success": props<{ documentId: number }>(),
    "Delete PI Document Failure": props<{ error: string }>(),


    // ============================================
    // STEP 7 - IMPROVEMENT ACTIVITIES (IA)
    // ============================================

    "Save IA Measurements": props<{
      packetId: number;
      performanceStart: Date;
      performanceEnd: Date;
      measurements: Array<{
        measureId: string;
        booleanValue: boolean;
      }>;
    }>(),
    "Save IA Measurements Success": props<{
      response: SaveIAMeasurementsResponse;
      packetId: number;
      measurements: Array<{
        measureId: string;
        booleanValue: boolean;
      }>;
      dateRangeStart: string;
      dateRangeEnd: string;
    }>(),
    "Save IA Measurements Failure": props<{ error: string }>(),

    "Upload IA Document": props<{
      packetId: number;
      measureId: string;
      file: File;
    }>(),
    "Upload IA Document Success": props<{
      packetId: number;
      documentId: number;
      fileName: string;
      measureId: string;
      documentUrl: string;
    }>(),
    "Upload IA Document Failure": props<{ error: string }>(),

    "Delete IA Document": props<{
      packetId: number;
      measureId: string;
    }>(),
    "Delete IA Document Success": props<{ packetId: number; measureId: string }>(),
    "Delete IA Document Failure": props<{ error: string }>(),
    "Download IA Document": props<{
      packetId: number;
      measureId: string;
      documentName: string;
      asAttachment: boolean;
    }>(),
    "Download IA Document Success": props<{ blob: Blob; fileName: string }>(),
    "Download IA Document Failure": props<{ error: string }>(),

    "View IA Document": props<{
      packetId: number;
      measureId: string;
      documentName: string;
    }>(),
    // ============================================
    // STEP 8 - DRCF
    // ============================================

    "Create DRCF Envelope": props<{
      submissionId: number;
      recipientEmail: string | null;
    }>(),
    "Create DRCF Envelope Success": props<{
      envelopeId: string;
      submissionId: number;
    }>(),
    "Create DRCF Envelope Failure": props<{ error: string }>(),

    "Load DRCF Envelopes": props<{ submissionId: number }>(),
    "Load DRCF Envelopes Success": props<{ submissionId: number; documents: DrcfDocument[] }>(),
    "Load DRCF Envelopes Failure": props<{ error: string }>(),

    "Open DRCF Signing": props<{ envelopeId: string }>(),
    "Open DRCF Signing Success": props<{ url: string }>(),
    "Open DRCF Signing Failure": props<{ error: string }>(),

    "Update DRCF Status": props<{ drcfStatus: DrcfStatus }>(),
    "Update DRCF Status Success": props<{ drcfStatus: DrcfStatus }>(),
    "Update DRCF Status Failure": props<{ error: string }>(),

    "Void DRCF Envelope": props<{
      submissionId: number;
      envelopeId: string;
      voidReason: string;
    }>(),
    "Void DRCF Envelope Success": emptyProps(),
    "Void DRCF Envelope Failure": props<{ error: string }>(),

    "Download DRCF Document": props<{
      submissionId: number;
      envelopeId: string;
    }>(),
    "Download DRCF Document Success": props<{
      blob: Blob;
      fileName: string;
    }>(),
    "Download DRCF Document Failure": props<{ error: string }>(),

    "Download DRCF Template": props<{ entityType: string }>(),
    "Download DRCF Template Success": props<{
      blob: Blob;
      fileName: string;
    }>(),
    "Download DRCF Template Failure": props<{ error: string }>(),

    // ============================================
    // SUBMISSION
    // ============================================

    "Submit Packet": props<{ packetId: number }>(),
    "Submit Packet Success": props<{ response: SubmitPacketResponse }>(),
    "Submit Packet Failure": props<{ error: string }>(),

    "Submit Multiple Packets": props<{ packetIds: number[] }>(),
    "Submit Multiple Packets Success": props<{
      responses: SubmitPacketResponse[];
    }>(),
    "Submit Multiple Packets Failure": props<{ error: string }>(),

    // ============================================
    // SUBMISSION SUCCESS DATA
    // ============================================
    "Set Submission Success Data": props<{
      packetId: number;
      tin: string;
      npi: string;
      isIndividual: boolean;
      performanceYear: number;
      externalSubmissionId: string;
      submittedDate: string;
    }>(),

    // ============================================
    // ORG SUBMISSIONS
    // ============================================
    "Load Org Submissions": props<{ orgId: string; performanceYear: number; tin: string }>(),
    "Load Org Submissions Success": props<{ submissions: any[] }>(),
    "Load Org Submissions Failure": props<{ error: string }>(),

    // ============================================
    // DELETE SUBMISSION
    // ============================================
    "Delete Submission": props<{ packetId: number; organizationId: number }>(),
    "Delete Submission Success": props<{ packetId: number }>(),
    "Delete Submission Failure": props<{ error: string }>(),

    // ============================================
    // SUBMISSION SCORE
    // ============================================
    "Get Submission Score": props<{ submissionId: number }>(),
    "Get Submission Score Success": props<{ score: GetSubmissionScoreResponse }>(),
    "Get Submission Score Failure": props<{ error: string }>(),
  },
});
